import { dynamicConfigManifest } from '@wac-web/utils/dynamic-configs/dynamic-config-manifest';
import { getDynamicConfig } from '@wac-web/utils/dynamic-configs/get-dynamic-config/get-dynamic-config';

import { FASTSHIFT_PATH_KEYS } from '@/common/routes/constants';

import { logFastshiftTraceOnce } from './fastshift-trace-dev-log';

/**
 * Runtime tracing overrides, read from the `fastshift_portal_tracing` dynamic config.
 *
 * The config is a positional string list:
 *   [0] `customTracingEnabled` — any other value disables the override entirely
 *   [1] sample percentage, an integer from 1 to 100
 *   [2] `allRoutes`, or the first route key to apply the percentage to
 *   [3+] further route keys
 *
 * When `tracingEnabled` is false, callers should fall back to normal tracing behaviour.
 */
export type FastshiftCustomTracingData = {
	tracingEnabled: boolean;
	sampleRate: number;
	allRoutes: boolean;
	specificRoutes: readonly FASTSHIFT_PATH_KEYS[];
};

const CUSTOM_TRACING_ENABLED_VALUE = 'customTracingEnabled';
const ALL_ROUTES_VALUE = 'allRoutes';
const MIN_SAMPLE_RATE = 1;
const MAX_SAMPLE_RATE = 100;
const INTEGER_ONLY = /^\d+$/;

export const FASTSHIFT_CUSTOM_TRACING_DISABLED: FastshiftCustomTracingData = Object.freeze({
	tracingEnabled: false,
	sampleRate: 0,
	allRoutes: false,
	specificRoutes: Object.freeze([]),
});

const FASTSHIFT_PATH_KEY_VALUES: readonly string[] = Object.values(FASTSHIFT_PATH_KEYS);

const isFastshiftPathKey = (value: string): value is FASTSHIFT_PATH_KEYS =>
	FASTSHIFT_PATH_KEY_VALUES.includes(value);

/**
 * Tested against the pattern before `Number()` so that values `Number()` would otherwise
 * accept — `'50.5'`, `' 50 '`, `'1e2'`, `'0x32'`, `'+50'`, `''` — are rejected outright.
 */
const parseSampleRate = (value: string | undefined): number | undefined => {
	if (value === undefined || !INTEGER_ONLY.test(value)) {
		return undefined;
	}

	const sampleRate = Number(value);

	return sampleRate >= MIN_SAMPLE_RATE && sampleRate <= MAX_SAMPLE_RATE ? sampleRate : undefined;
};

export function parseFastshiftCustomTracingData(values: string[]): FastshiftCustomTracingData {
	const [enabledValue, rawSampleRate, ...routeValues] = values;

	if (enabledValue !== CUSTOM_TRACING_ENABLED_VALUE) {
		return FASTSHIFT_CUSTOM_TRACING_DISABLED;
	}

	const sampleRate = parseSampleRate(rawSampleRate);

	if (sampleRate === undefined) {
		return FASTSHIFT_CUSTOM_TRACING_DISABLED;
	}

	if (routeValues[0] === ALL_ROUTES_VALUE) {
		return { tracingEnabled: true, sampleRate, allRoutes: true, specificRoutes: [] };
	}

	const specificRoutes = [...new Set(routeValues.filter(isFastshiftPathKey))];

	// A percentage with no route to apply it to is an incomplete config, not an override.
	if (specificRoutes.length === 0) {
		return FASTSHIFT_CUSTOM_TRACING_DISABLED;
	}

	return { tracingEnabled: true, sampleRate, allRoutes: false, specificRoutes };
}

export function getFastshiftCustomTracingData(): FastshiftCustomTracingData {
	const values = getDynamicConfig({
		config: dynamicConfigManifest.fastshiftPortalTracing,
		defaultValue: [],
	});
	const tracingData = parseFastshiftCustomTracingData(values);

	// Logged on first read rather than at install time: the interceptor is installed before
	// `DynamicConfigClient` has initialised, so anything logged earlier would report the empty
	// default instead of the real config. `raw` is included so a config that silently parses to
	// disabled — a typo in `customTracingEnabled`, an out-of-range percentage — is diagnosable.
	logFastshiftTraceOnce(
		'dynamic-config',
		`dynamic config '${dynamicConfigManifest.fastshiftPortalTracing.configKey}' resolved`,
		{ raw: values, parsed: tracingData },
	);

	return tracingData;
}
